import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { UploadCloud, X, Image } from "lucide-react";
import {
    uploadVideoFileInspirational,
    uploadVideoUrlInspirational,
    addInspirationalVideo,
    upload_thumbnail
} from "../../requests";
import { notifyError, notifySuccess } from "../../utils/helper";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { useDispatch } from "react-redux";
import styles from "../../_css/ContenetManagement.module.css";

const AddInspirationalVideo = ({ show, onHide, onSuccess }) => {
    const [videoFile, setVideoFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [url, setUrl] = useState("");
    const [formData, setFormData] = useState({
        title: "",
        subTitle: "",
        duration: "",
        speaker: ""
    });
    const [isHovered, setIsHovered] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [thumbnailUploading, setThumbnailUploading] = useState(false);
    const dispatch = useDispatch();

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validVideoTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/mkv', 'video/webm'];
            if (!validVideoTypes.includes(file.type)) {
                notifyError("Please upload a valid video file (MP4, MOV, AVI, MKV, WEBM)");
                return;
            }

            const maxSize = 50 * 1024 * 1024;
            if (file.size > maxSize) {
                notifyError("File size must be less than 50MB");
                return;
            }

            setVideoFile(file);
            setUrl("");
        }
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validImageTypes.includes(file.type)) {
                notifyError("Please upload a valid image file (JPEG, PNG, GIF, WEBP)");
                return;
            }

            const maxSize = 5 * 1024 * 1024; // 5MB for thumbnails
            if (file.size > maxSize) {
                notifyError("Thumbnail size must be less than 5MB");
                return;
            }

            setThumbnailFile(file);
        }
    };

    const handleUrlChange = (e) => {
        setUrl(e.target.value);
        setVideoFile(null);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const triggerFileInput = () => document.getElementById("upload-video").click();
    const triggerThumbnailInput = () => document.getElementById("upload-thumbnail").click();

    const removeThumbnail = () => {
        setThumbnailFile(null);
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            notifyError("Please enter a title");
            return false;
        }
        if (!formData.subTitle.trim()) {
            notifyError("Please enter a sub title");
            return false;
        }
        if (!formData.speaker.trim()) {
            notifyError("Please enter speaker name");
            return false;
        }
        if (!formData.duration.trim()) {
            notifyError("Please enter duration");
            return false;
        }
        if (!videoFile && !url.trim()) {
            notifyError("Please upload a video file or enter a URL");
            return false;
        }
        return true;
    };

    const handleUploadUrl = async () => {
        if (!url.trim()) {
            notifyError("Please enter a URL");
            return;
        }

        try {
            dispatch(startLoader());

            const urlPattern = /^(https?:\/\/)/;
            if (!urlPattern.test(url)) {
                notifyError("Please enter a valid URL starting with http:// or https://");
                return;
            }
            const result = await uploadVideoUrlInspirational(url);
            setUrl(result.url);
            notifySuccess("URL uploaded successfully!");
        } catch (err) {
            console.error("URL upload error:", err);
            notifyError(err.response?.data?.message || err.message || "URL upload failed");
        } finally {
            dispatch(stopLoader());
        }
    };

    const uploadThumbnail = async (file) => {
        try {
            setThumbnailUploading(true);
            const thumbnailResponse = await upload_thumbnail(file);
            console.log("Thumbnail upload response: ", thumbnailResponse);
            return thumbnailResponse.blobUrl; // Adjust based on your API response structure
        } catch (error) {
            console.error("Thumbnail upload error:", error);
            notifyError("Failed to upload thumbnail");
            throw error;
        } finally {
            setThumbnailUploading(false);
        }
    };

    const handleSaveOrPublish = async (status) => {
        if (!validateForm()) return;

        try {
            dispatch(startLoader());
            let videoUrl = url;
            let thumbnailUrl = "";

            // Upload thumbnail first if provided
            if (thumbnailFile) {
                try {
                    thumbnailUrl = await uploadThumbnail(thumbnailFile);
                    notifySuccess("Thumbnail uploaded successfully!");
                } catch (thumbnailError) {
                    console.error("Thumbnail upload failed:", thumbnailError);
                    // Continue without thumbnail if upload fails
                    notifyError("Thumbnail upload failed, continuing without thumbnail");
                }
            }

            // Upload video file if provided
            if (videoFile) {
                setUploading(true);
                setUploadProgress(0);

                try {
                    const uploadResult = await uploadVideoFileInspirational(videoFile, (progress) => {
                        setUploadProgress(progress);
                    });
                    console.log("Uploaded video result: ", uploadResult)
                    setUploading(false);
                    setUploadProgress(100);
                    notifySuccess("Video uploaded successfully!");
                    videoUrl = uploadResult.blobUrl;
                } catch (uploadError) {
                    setUploading(false);
                    console.error("Video upload error:", uploadError);
                    notifyError(uploadError.response?.data?.message || uploadError.message || "Video upload failed");
                    return;
                }
            }

            const payload = {
                title: formData.title.trim(),
                sub_title: formData.subTitle.trim(),
                speaker: formData.speaker.trim(),
                duration: formData.duration.trim(),
                video_url: videoUrl,
                thumbnail: thumbnailUrl,
                status
            };

            await addInspirationalVideo(payload);

            notifySuccess(`Video ${status === "Draft" ? "saved as draft" : "published"} successfully!`);

            if (onSuccess) {
                onSuccess();
            }

            handleClose();
        } catch (err) {
            console.error("Save/Publish error:", err);
            notifyError(err.response?.data?.message || err.message || `Failed to ${status === "Draft" ? "save draft" : "publish"} video`);
        } finally {
            dispatch(stopLoader());
            setUploading(false);
            setThumbnailUploading(false);
        }
    };

    const handleClose = () => {
        setFormData({ title: "", subTitle: "", duration: "", speaker: "" });
        setVideoFile(null);
        setThumbnailFile(null);
        setUrl("");
        setUploadProgress(0);
        setUploading(false);
        setThumbnailUploading(false);
        onHide();
    };

    const isFormDisabled = uploading || thumbnailUploading;

    return (
        <Modal show={show} onHide={handleClose} centered dialogClassName="modal-38">
            <Modal.Body className={isFormDisabled ? styles.addInspirationalDisabled : ""}>
                <div className={styles.addInspirationalModalContainer}>
                    <div className={styles.addInspirationalHeader}>
                        <X onClick={handleClose} className={styles.addInspirationalCloseIcon} />
                        <span className={styles.addInspirationalTitle}>
                            Add Video
                        </span>
                    </div>

                    {/* Title */}
                    <div className={styles.addInspirationalFormGroup}>
                        <label className={styles.addInspirationalLabel}>Title</label>
                        <input
                            type="text"
                            placeholder="Ted Talk"
                            className={styles.addInspirationalInput}
                            value={formData.title}
                            onChange={(e) => handleInputChange("title", e.target.value)}
                            disabled={isFormDisabled}
                        />
                    </div>

                    {/* Sub Title */}
                    <div className={styles.addInspirationalFormGroup}>
                        <label className={styles.addInspirationalLabel}>Sub Title</label>
                        <input
                            type="text"
                            placeholder="How to improve Communication"
                            className={styles.addInspirationalInput}
                            value={formData.subTitle}
                            onChange={(e) => handleInputChange("subTitle", e.target.value)}
                            disabled={isFormDisabled}
                        />
                    </div>

                    {/* Speaker */}
                    <div className={styles.addInspirationalFormGroup}>
                        <label className={styles.addInspirationalLabel}>Speaker</label>
                        <input
                            type="text"
                            placeholder="Speaker name"
                            className={styles.addInspirationalInput}
                            value={formData.speaker}
                            onChange={(e) => handleInputChange("speaker", e.target.value)}
                            disabled={isFormDisabled}
                        />
                    </div>

                    {/* Duration & File Upload */}
                    <div className={styles.addInspirationalRow}>
                        <div className={styles.addInspirationalColumn}>
                            <label className={styles.addInspirationalLabel}>Duration</label>
                            <input
                                type="text"
                                placeholder="2 min"
                                className={styles.addInspirationalInput}
                                value={formData.duration}
                                onChange={(e) => handleInputChange("duration", e.target.value)}
                                disabled={isFormDisabled}
                            />
                        </div>
                        <div className={styles.addInspirationalColumn}>
                            <label className={styles.addInspirationalLabel}>Upload Video</label>
                            <div
                                className={`${styles.addInspirationalUploadArea} ${isFormDisabled ? styles.addInspirationalUploadAreaDisabled : ''}`}
                                onClick={isFormDisabled ? undefined : triggerFileInput}
                            >
                                <UploadCloud size={20} color="#000000" />
                                {videoFile ? (
                                    <span className={styles.addInspirationalFileName}>{videoFile.name}</span>
                                ) : (
                                    <span className={styles.addInspirationalUploadText}>Click to upload video</span>
                                )}
                            </div>
                            <input
                                type="file"
                                accept="video/*"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                id="upload-video"
                                disabled={isFormDisabled}
                            />



                            {/* {uploadProgress === 100 && !uploading && (
                                <p className={styles.addInspirationalSuccessMessage}>✅ Video uploaded successfully</p>
                            )} */}
                        </div>
                    </div>

                    {/* Thumbnail Upload */}
                    <div className={styles.addInspirationalFormGroup}>
                        <label className={styles.addInspirationalLabel}>
                            Upload Thumbnail {thumbnailUploading && "(Uploading...)"}
                        </label>
                        <div className={styles.addInspirationalThumbnailContainer}>
                            {thumbnailFile ? (
                                <div className={styles.addInspirationalThumbnailPreview}>
                                    <img
                                        src={URL.createObjectURL(thumbnailFile)}
                                        alt="Thumbnail preview"
                                        className={styles.addInspirationalThumbnailImage}
                                    />
                                    <div className={styles.addInspirationalThumbnailActions}>
                                        <span className={styles.addInspirationalFileName}>{thumbnailFile.name}</span>
                                        <X
                                            size={16}
                                            onClick={removeThumbnail}
                                            className={styles.addInspirationalRemoveThumbnail}
                                            disabled={isFormDisabled}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={`${styles.addInspirationalUploadArea} ${isFormDisabled ? styles.addInspirationalUploadAreaDisabled : ''}`}
                                    onClick={isFormDisabled ? undefined : triggerThumbnailInput}
                                >
                                    <Image size={20} color="#000000" />
                                    <span className={styles.addInspirationalUploadText}>Click to upload thumbnail</span>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                                style={{ display: 'none' }}
                                id="upload-thumbnail"
                                disabled={isFormDisabled}
                            />
                        </div>
                        <p className={styles.addInspirationalHelpText}>
                            Recommended: 16:9 aspect ratio, max 5MB (JPEG, PNG, GIF, WEBP)
                        </p>
                    </div>

                    {/* URL Upload */}
                    <div className={styles.addInspirationalFormGroup}>
                        <label className={styles.addInspirationalLabel}>or Upload the URL</label>
                        <div className={styles.addInspirationalUrlContainer}>
                            <input
                                type="text"
                                placeholder="https://www.example.com/video.mp4"
                                value={url}
                                onChange={handleUrlChange}
                                className={styles.addInspirationalUrlInput}
                                disabled={isFormDisabled || !!videoFile}
                            />
                            {/* <button
                                className={styles.addInspirationalUploadButton}
                                onMouseEnter={() => !isFormDisabled && setIsHovered(true)}
                                onMouseLeave={() => !isFormDisabled && setIsHovered(false)}
                                onClick={handleUploadUrl}
                                disabled={isFormDisabled || !url.trim() || !!videoFile}
                            >
                                Upload
                            </button> */}
                        </div>
                    </div>

                    {/* Save & Publish Buttons */}
                    <div className={styles.addInspirationalButtonContainer}>
                        <button
                            className={styles.addInspirationalDraftButton}
                            onClick={() => handleSaveOrPublish("Draft")}
                            disabled={isFormDisabled}
                        >
                            {uploading || thumbnailUploading ? "Uploading..." : "Save As Draft"}
                        </button>
                        <button
                            className={styles.addInspirationalPublishButton}
                            onClick={() => handleSaveOrPublish("Published")}
                            disabled={isFormDisabled}
                        >
                            {uploading || thumbnailUploading ? "Uploading..." : "Publish"}
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default AddInspirationalVideo;